/**
 * Question-text sanitizer (Feb 2026).
 *
 * Shared helper for rendering author-written question / sub-text /
 * guidance strings that may contain pasted Word/browser HTML.
 *
 * Rules mirror the backend `utils/sanitize.py`:
 *  • Preserve  <p> <br> <strong> <b> <em> <i> <u> <ul> <ol> <li>
 *  • Drop      every other tag, all attributes (style/class/id/on*),
 *              scripts/styles (contents included), comments, and Word
 *              paste garbage (`class="MsoNormal"`, `<o:p>`, mso-* …).
 *
 * Never render user-authored HTML without piping it through here.
 */

const ALLOWED_TAGS = new Set([
  'p', 'br',
  'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li',
]);

const VOID_TAGS = new Set(['br']);

// Tags whose CONTENTS are dropped entirely.
const BLOCK_CONTENT_RE = /<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

// Any HTML comment.
const COMMENT_RE = /<!--[\s\S]*?-->/g;

// Tag matcher (opening / closing / self-closing). Captures the tag name.
const TAG_RE = /<\/?\s*([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;


/**
 * Return a whitelisted HTML string. Non-string / empty inputs pass
 * through unchanged.
 */
export function sanitizeHtml(input) {
  if (input == null) return input;
  if (typeof input !== 'string') return input;
  if (!input.trim()) return '';

  // 1. Kill <script>/<style> blocks (contents + wrapper) and comments.
  let out = String(input)
    .replace(BLOCK_CONTENT_RE, '')
    .replace(COMMENT_RE, '');

  // 2. For every remaining tag, keep it only if it's on the whitelist —
  //    and strip its attributes entirely.
  out = out.replace(TAG_RE, (match, name) => {
    const t = name.toLowerCase();
    if (!ALLOWED_TAGS.has(t)) return '';
    const isClosing = match.startsWith('</');
    if (isClosing) return `</${t}>`;
    if (VOID_TAGS.has(t)) return `<${t}/>`;
    return `<${t}>`;
  });

  // 3. Collapse runs of whitespace introduced by the strip.
  return out.replace(/[ \t]{2,}/g, ' ').replace(/\s+\n/g, '\n').trim();
}


/**
 * Return plain text — every tag removed, entities decoded. Used by
 * contexts that must not show any markup (Experience Performance).
 */
export function stripHtml(input) {
  if (input == null) return input;
  if (typeof input !== 'string') return input;
  const noBlocks = input.replace(BLOCK_CONTENT_RE, '');
  const noTags = noBlocks.replace(/<[^>]+>/g, '');
  // Decode entities via the browser parser when available.
  if (typeof document !== 'undefined') {
    const el = document.createElement('textarea');
    el.innerHTML = noTags;
    return el.value.replace(/\s+/g, ' ').trim();
  }
  return noTags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}


/**
 * Convenience for `dangerouslySetInnerHTML`. Always pipe untrusted /
 * author-authored strings through this instead of the raw value.
 */
export function safeHtml(input) {
  return { __html: sanitizeHtml(input) || '' };
}
